/**
 * Web Serial API — подключение GS1 DataMatrix сканера прямо из браузера.
 *
 * Работает в Chrome/Edge при secure context (HTTPS или localhost).
 * Не поддерживается в Firefox/Safari — там кнопка будет disabled.
 *
 * Поток байт со сканера: после каждого скана приходит полная GS1-запись
 * формата `01<GTIN-14>21<serial>[GS]91<crypto>[GS]92<crypto>`, где GS = 0x1D
 * (FNC1 в GS1 DataMatrix), завершается CR/LF (суффикс postamble сканера).
 * Мы накапливаем байты в буфере, разрезаем по CR/LF и каждую полную запись
 * парсим в composite_key `<GTIN>:<serial>`, идентичный тому, что backend
 * кладёт в Code.composite_key при импорте CSV от TechShare.
 */

const GS = 0x1d;
const CR = 0x0d;
const LF = 0x0a;

export interface SerialConnectOptions {
  /** baud rate сканера; у большинства Honeywell/Zebra в Serial-режиме это 9600 */
  baudRate?: number;
}

export interface ParsedScan {
  gtin: string;
  serial: string;
  compositeKey: string;
  raw: Uint8Array;
}

export class ScanParseError extends Error {
  raw: Uint8Array;
  constructor(message: string, raw: Uint8Array) {
    super(message);
    this.raw = raw;
  }
}

export function parseGs1Scan(bytes: Uint8Array): ParsedScan {
  // trim trailing CR/LF/space
  let end = bytes.length;
  while (end > 0 && (bytes[end - 1] === CR || bytes[end - 1] === LF || bytes[end - 1] === 0x20)) {
    end--;
  }
  const buf = bytes.subarray(0, end);

  if (buf.length < 2 + 14 + 2 + 1) {
    throw new ScanParseError("скан слишком короткий", buf);
  }
  // AI 01
  if (buf[0] !== 0x30 /* '0' */ || buf[1] !== 0x31 /* '1' */) {
    throw new ScanParseError("не начинается с AI 01 (GTIN)", buf);
  }
  const gtinBytes = buf.subarray(2, 16);
  for (const b of gtinBytes) {
    if (b < 0x30 || b > 0x39) {
      throw new ScanParseError("GTIN не состоит из цифр", buf);
    }
  }
  const gtin = new TextDecoder("ascii").decode(gtinBytes);
  // AI 21
  if (buf[16] !== 0x32 /* '2' */ || buf[17] !== 0x31 /* '1' */) {
    throw new ScanParseError("после GTIN ожидался AI 21 (serial)", buf);
  }
  // serial идёт до ближайшего GS или до конца
  let gsIdx = -1;
  for (let i = 18; i < buf.length; i++) {
    if (buf[i] === GS) {
      gsIdx = i;
      break;
    }
  }
  const serialBytes = gsIdx === -1 ? buf.subarray(18) : buf.subarray(18, gsIdx);
  if (serialBytes.length === 0) {
    throw new ScanParseError("пустой serial", buf);
  }
  const serial = new TextDecoder("ascii").decode(serialBytes);
  return {
    gtin,
    serial,
    compositeKey: `${gtin}:${serial}`,
    raw: buf,
  };
}

export type ScannerStatus =
  | { kind: "unsupported" }
  | { kind: "disconnected" }
  | { kind: "connecting" }
  | { kind: "connected" }
  | { kind: "error"; message: string };

export interface ScannerCallbacks {
  onStatusChange(status: ScannerStatus): void;
  onScan(scan: ParsedScan): void;
  onError(message: string, raw?: Uint8Array): void;
}

/** Одноэкземплярный коннектор. Не открывает порт повторно. */
export class ScannerConnection {
  private port: SerialPort | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private buffer: number[] = [];
  private stopped = false;

  constructor(private cb: ScannerCallbacks) {}

  static isSupported(): boolean {
    return typeof navigator !== "undefined" && "serial" in navigator;
  }

  isOpen(): boolean {
    return this.port !== null && !this.stopped;
  }

  async connect(opts: SerialConnectOptions = {}): Promise<void> {
    if (!ScannerConnection.isSupported()) {
      this.cb.onStatusChange({ kind: "unsupported" });
      return;
    }
    if (this.port) return;
    this.cb.onStatusChange({ kind: "connecting" });
    try {
      const port = await navigator.serial.requestPort();
      await port.open({ baudRate: opts.baudRate ?? 9600 });
      this.port = port;
      this.stopped = false;
      this.cb.onStatusChange({ kind: "connected" });
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.readLoop();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      this.cb.onStatusChange({ kind: "error", message: msg });
    }
  }

  async disconnect(): Promise<void> {
    this.stopped = true;
    try {
      await this.reader?.cancel();
    } catch {
      /* ignore */
    }
    try {
      await this.port?.close();
    } catch {
      /* ignore */
    }
    this.port = null;
    this.reader = null;
    this.buffer = [];
    this.cb.onStatusChange({ kind: "disconnected" });
  }

  private async readLoop(): Promise<void> {
    if (!this.port?.readable) return;
    const reader = this.port.readable.getReader();
    this.reader = reader;
    try {
      while (!this.stopped) {
        const { value, done } = await reader.read();
        if (done) break;
        if (!value) continue;
        for (const b of value) {
          this.buffer.push(b);
          if (b === LF || b === CR) {
            // откусить всё до и включая этот байт, проверяя что чем-то помимо CRLF
            this.flushRecord();
          }
        }
        // safety net: если сканер не шлёт CR/LF, но внутри буфера уже есть целая запись,
        // попробуем её распарсить при накоплении 40+ байт без терминатора
        if (this.buffer.length > 256) {
          this.flushRecord(true);
        }
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (!this.stopped) {
        this.cb.onStatusChange({ kind: "error", message: msg });
      }
    } finally {
      try {
        reader.releaseLock();
      } catch {
        /* ignore */
      }
    }
  }

  private flushRecord(force = false): void {
    // отрезать trailing CR/LF
    let end = this.buffer.length;
    while (end > 0 && (this.buffer[end - 1] === CR || this.buffer[end - 1] === LF)) {
      end--;
    }
    if (end === 0 && !force) {
      this.buffer = [];
      return;
    }
    const record = new Uint8Array(this.buffer.slice(0, end));
    this.buffer = [];
    if (record.length === 0) return;
    try {
      const parsed = parseGs1Scan(record);
      this.cb.onScan(parsed);
    } catch (e) {
      if (e instanceof ScanParseError) {
        this.cb.onError(e.message, e.raw);
      } else {
        this.cb.onError(e instanceof Error ? e.message : String(e));
      }
    }
  }
}
